import { GazeDirectionVector, GazeWithDelta, PercentagePoint } from "types/testTypes";

const MIN_SAMPLE_DURATION = 0.001;

export interface FixationSaccadeConfig {
  /** Minimum duration (in seconds) for a group of samples to qualify as a fixation */
  minFixationDuration: number;
  /** Minimum number of contiguous gaze samples required for a fixation */
  minSamplesPerFixation: number;
  /** Maximum dispersion (in percentage units) allowed within a fixation window */
  dispersionThreshold: number;
  /**
   * Nominal device sampling rate in Hz.
   * Used only as a fallback when cadence cannot be inferred from the observed sample deltas.
   */
  samplingRateHz: number;
  /**
   * Intervals larger than this multiple of the nominal sample duration are treated as dropped-frame gaps.
   */
  dropoutThresholdMultiplier: number;
  /** Horizontal field of view of the headset in degrees */
  headsetHFOV: number;
  /** Vertical field of view of the headset in degrees */
  headsetVFOV: number;
  /** Merge spatial threshold in degrees of visual angle */
  mergeDistanceDegrees: number;
}

const DEFAULT_FIXATION_SACCADE_CONFIG: FixationSaccadeConfig = {
  minFixationDuration: 0.08,
  minSamplesPerFixation: 2,
  dispersionThreshold: 3,
  samplingRateHz: 30,
  dropoutThresholdMultiplier: 1.75,
  headsetHFOV: 100,
  headsetVFOV: 100,
  mergeDistanceDegrees: 0.5,
};

interface EyeMovementSample {
  index: number;
  point: PercentagePoint;
  duration: number;
  rawDuration: number;
  gapAfterDuration: number;
  hasDropoutAfter: boolean;
  startTime: number;
  endTime: number;
  gazeDirection?: GazeDirectionVector;
  leftGazeDirection?: GazeDirectionVector;
  rightGazeDirection?: GazeDirectionVector;
  gazeOrigin?: GazeDirectionVector;
  headPosition?: GazeDirectionVector;
  headAngularSpeed?: number;
  leftEyeOpenness?: number;
  rightEyeOpenness?: number;
  leftPupilMm?: number;
  rightPupilMm?: number;
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const sanitizeDuration = (value: unknown): number => {
  if (!isFiniteNumber(value) || value <= 0) {
    return MIN_SAMPLE_DURATION;
  }
  return Math.max(value, MIN_SAMPLE_DURATION);
};

const calculateMedian = (values: number[]): number | undefined => {
  if (!values.length) {
    return undefined;
  }

  const ordered = values.slice().sort((a, b) => a - b);
  const mid = Math.floor(ordered.length / 2);

  if (ordered.length % 2 === 0) {
    return (ordered[mid - 1] + ordered[mid]) / 2;
  }

  return ordered[mid];
};

const normalizeMinimumSampleCount = (value: number | undefined): number => {
  if (!isFiniteNumber(value)) {
    return DEFAULT_FIXATION_SACCADE_CONFIG.minSamplesPerFixation;
  }

  return Math.max(1, Math.floor(value));
};

const normalizeDropoutThresholdMultiplier = (value: number | undefined): number => {
  if (!isFiniteNumber(value) || value <= 1) {
    return DEFAULT_FIXATION_SACCADE_CONFIG.dropoutThresholdMultiplier;
  }

  return value;
};

const extractPositiveSampleDurations = (gazeData: GazeWithDelta[]): number[] =>
  gazeData
    .map((entry) => entry?.delta)
    .filter((value): value is number => isFiniteNumber(value) && value > 0)
    .map((value) => Math.max(value, MIN_SAMPLE_DURATION));

const isPercentagePoint = (value: unknown): value is PercentagePoint =>
  Boolean(
    value &&
    isFiniteNumber((value as PercentagePoint).xPercentage) &&
    isFiniteNumber((value as PercentagePoint).yPercentage),
  );

const normalizePercentageCoordinate = (value: number): number => {
  if (!isFiniteNumber(value)) {
    return 0;
  }
  if (Math.abs(value) <= 1) {
    return value * 100;
  }
  return value;
};

/**
 * Reject gaze samples that are likely tracking-lost artifacts.
 *
 * ASSUMPTION: The device sends (0, 0) or out-of-range values when tracking is lost.
 * If the device uses a different sentinel value, update this function.
 */
const isValidGazeSample = (point: PercentagePoint): boolean => {
  const x = point.xPercentage;
  const y = point.yPercentage;

  // Reject NaN / Infinity
  if (!isFiniteNumber(x) || !isFiniteNumber(y)) return false;

  // Reject (0, 0) — likely tracking-lost sentinel
  if (x === 0 && y === 0) return false;

  // Reject out-of-bounds values
  if (x < 0 || x > 100 || y < 0 || y > 100) return false;

  return true;
};

const distanceBetweenPoints = (from: PercentagePoint, to: PercentagePoint): number => {
  const dx = to.xPercentage - from.xPercentage;
  const dy = to.yPercentage - from.yPercentage;
  return Math.sqrt(dx * dx + dy * dy);
};

const calculateDispersion = (samples: EyeMovementSample[]): number => {
  if (samples.length <= 1) {
    return 0;
  }
  let minX = samples[0].point.xPercentage;
  let maxX = samples[0].point.xPercentage;
  let minY = samples[0].point.yPercentage;
  let maxY = samples[0].point.yPercentage;

  for (let i = 1; i < samples.length; i += 1) {
    const { xPercentage, yPercentage } = samples[i].point;
    minX = Math.min(minX, xPercentage);
    maxX = Math.max(maxX, xPercentage);
    minY = Math.min(minY, yPercentage);
    maxY = Math.max(maxY, yPercentage);
  }

  return maxX - minX + (maxY - minY);
};

const calculateWeightedCentroid = (samples: EyeMovementSample[]): PercentagePoint => {
  if (!samples.length) {
    return { xPercentage: 0, yPercentage: 0 };
  }

  const totalDuration = samples.reduce((sum, sample) => sum + sample.duration, 0);

  if (totalDuration <= 0) {
    const avgX =
      samples.reduce((sum, sample) => sum + sample.point.xPercentage, 0) / samples.length;
    const avgY =
      samples.reduce((sum, sample) => sum + sample.point.yPercentage, 0) / samples.length;
    return { xPercentage: avgX, yPercentage: avgY };
  }

  const weightedX = samples.reduce(
    (sum, sample) => sum + sample.point.xPercentage * sample.duration,
    0,
  );
  const weightedY = samples.reduce(
    (sum, sample) => sum + sample.point.yPercentage * sample.duration,
    0,
  );

  return {
    xPercentage: weightedX / totalDuration,
    yPercentage: weightedY / totalDuration,
  };
};

const isValidGazeDirection = (v: unknown): v is GazeDirectionVector => {
  if (!v || typeof v !== "object") return false;
  const { x, y, z } = v as GazeDirectionVector;
  return (
    isFiniteNumber(x) && isFiniteNumber(y) && isFiniteNumber(z) && (x !== 0 || y !== 0 || z !== 0)
  );
};

const calculateWeightedDirectionCentroid = (
  samples: EyeMovementSample[],
): GazeDirectionVector | undefined => {
  const withDir = samples.filter((s) => s.gazeDirection);
  if (!withDir.length) return undefined;

  const totalDuration = withDir.reduce((sum, s) => sum + s.duration, 0);

  if (totalDuration <= 0) {
    const n = withDir.length;
    return {
      x: withDir.reduce((sum, s) => sum + s.gazeDirection!.x, 0) / n,
      y: withDir.reduce((sum, s) => sum + s.gazeDirection!.y, 0) / n,
      z: withDir.reduce((sum, s) => sum + s.gazeDirection!.z, 0) / n,
    };
  }

  return {
    x: withDir.reduce((sum, s) => sum + s.gazeDirection!.x * s.duration, 0) / totalDuration,
    y: withDir.reduce((sum, s) => sum + s.gazeDirection!.y * s.duration, 0) / totalDuration,
    z: withDir.reduce((sum, s) => sum + s.gazeDirection!.z * s.duration, 0) / totalDuration,
  };
};

const computeBiometrics = (samples: EyeMovementSample[]): FixationBiometrics | undefined => {
  const bio: FixationBiometrics = {};
  let hasBio = false;

  const validValues = (extractor: (s: EyeMovementSample) => number | undefined): number[] =>
    samples.map(extractor).filter((v): v is number => v !== undefined);

  const mean = (values: number[]): number | undefined =>
    values.length ? values.reduce((a, b) => a + b, 0) / values.length : undefined;

  const leftPupils = validValues((s) => s.leftPupilMm);
  const rightPupils = validValues((s) => s.rightPupilMm);
  const leftOpenness = validValues((s) => s.leftEyeOpenness);
  const rightOpenness = validValues((s) => s.rightEyeOpenness);

  if (leftPupils.length) {
    bio.meanLeftPupilMm = mean(leftPupils);
    hasBio = true;
  }
  if (rightPupils.length) {
    bio.meanRightPupilMm = mean(rightPupils);
    hasBio = true;
  }
  if (leftOpenness.length) {
    bio.meanLeftEyeOpenness = mean(leftOpenness);
    hasBio = true;
  }
  if (rightOpenness.length) {
    bio.meanRightEyeOpenness = mean(rightOpenness);
    hasBio = true;
  }

  const withLeftDir = samples.filter((s) => s.leftGazeDirection);
  if (withLeftDir.length) {
    const n = withLeftDir.length;
    bio.leftGazeDirection = {
      x: withLeftDir.reduce((sum, s) => sum + s.leftGazeDirection!.x, 0) / n,
      y: withLeftDir.reduce((sum, s) => sum + s.leftGazeDirection!.y, 0) / n,
      z: withLeftDir.reduce((sum, s) => sum + s.leftGazeDirection!.z, 0) / n,
    };
    hasBio = true;
  }

  const withRightDir = samples.filter((s) => s.rightGazeDirection);
  if (withRightDir.length) {
    const n = withRightDir.length;
    bio.rightGazeDirection = {
      x: withRightDir.reduce((sum, s) => sum + s.rightGazeDirection!.x, 0) / n,
      y: withRightDir.reduce((sum, s) => sum + s.rightGazeDirection!.y, 0) / n,
      z: withRightDir.reduce((sum, s) => sum + s.rightGazeDirection!.z, 0) / n,
    };
    hasBio = true;
  }

  const withGazeOrigin = samples.filter((s) => s.gazeOrigin);
  if (withGazeOrigin.length) {
    const n = withGazeOrigin.length;
    bio.meanGazeOrigin = {
      x: withGazeOrigin.reduce((sum, s) => sum + s.gazeOrigin!.x, 0) / n,
      y: withGazeOrigin.reduce((sum, s) => sum + s.gazeOrigin!.y, 0) / n,
      z: withGazeOrigin.reduce((sum, s) => sum + s.gazeOrigin!.z, 0) / n,
    };
    hasBio = true;
  }

  const withHead = samples.filter((s) => s.headPosition);
  if (withHead.length) {
    const n = withHead.length;
    bio.meanHeadPosition = {
      x: withHead.reduce((sum, s) => sum + s.headPosition!.x, 0) / n,
      y: withHead.reduce((sum, s) => sum + s.headPosition!.y, 0) / n,
      z: withHead.reduce((sum, s) => sum + s.headPosition!.z, 0) / n,
    };
    hasBio = true;
  }

  const headAngSpeeds = validValues((s) => s.headAngularSpeed);
  if (headAngSpeeds.length) {
    bio.meanHeadAngularSpeed = mean(headAngSpeeds);
    hasBio = true;
  }

  return hasBio ? bio : undefined;
};

/**
 * Compute the angular distance in degrees between two 3D gaze direction vectors
 * using arccos(dot / (|a|·|b|)). Scale-invariant — the ×2 calibration scaling cancels out.
 */
const angularDistanceDegrees = (a: GazeDirectionVector, b: GazeDirectionVector): number => {
  const dot = a.x * b.x + a.y * b.y + a.z * b.z;
  const magA = Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);
  const magB = Math.sqrt(b.x * b.x + b.y * b.y + b.z * b.z);
  if (magA === 0 || magB === 0) return 0;
  const cosAngle = Math.min(1, Math.max(-1, dot / (magA * magB)));
  return Math.acos(cosAngle) * (180 / Math.PI);
};

export interface FixationBiometrics {
  meanLeftPupilMm?: number;
  meanRightPupilMm?: number;
  meanLeftEyeOpenness?: number;
  meanRightEyeOpenness?: number;
  meanGazeOrigin?: GazeDirectionVector;
  meanHeadPosition?: GazeDirectionVector;
  meanHeadAngularSpeed?: number;
  leftGazeDirection?: GazeDirectionVector;
  rightGazeDirection?: GazeDirectionVector;
}

export class Fixation {
  constructor(
    public readonly index: number,
    public readonly startSampleIndex: number,
    public readonly endSampleIndex: number,
    public readonly position: PercentagePoint,
    public readonly duration: number,
    public readonly startTime: number,
    public readonly endTime: number,
    public readonly gazeDirection?: GazeDirectionVector,
    public readonly biometrics?: FixationBiometrics,
  ) {}

  withIndex(newIndex: number): Fixation {
    return new Fixation(
      newIndex,
      this.startSampleIndex,
      this.endSampleIndex,
      this.position,
      this.duration,
      this.startTime,
      this.endTime,
      this.gazeDirection,
      this.biometrics,
    );
  }

  static merge(first: Fixation, second: Fixation, index: number): Fixation {
    const totalWeight = first.duration + second.duration;
    const weightedX =
      totalWeight > 0
        ? (first.position.xPercentage * first.duration +
            second.position.xPercentage * second.duration) /
          totalWeight
        : (first.position.xPercentage + second.position.xPercentage) / 2;
    const weightedY =
      totalWeight > 0
        ? (first.position.yPercentage * first.duration +
            second.position.yPercentage * second.duration) /
          totalWeight
        : (first.position.yPercentage + second.position.yPercentage) / 2;

    const startTime = first.startTime;
    const endTime = Math.max(second.endTime, startTime + MIN_SAMPLE_DURATION);
    const duration = Math.max(endTime - startTime, MIN_SAMPLE_DURATION);

    let mergedDirection: GazeDirectionVector | undefined;
    if (first.gazeDirection && second.gazeDirection) {
      if (totalWeight > 0) {
        mergedDirection = {
          x:
            (first.gazeDirection.x * first.duration + second.gazeDirection.x * second.duration) /
            totalWeight,
          y:
            (first.gazeDirection.y * first.duration + second.gazeDirection.y * second.duration) /
            totalWeight,
          z:
            (first.gazeDirection.z * first.duration + second.gazeDirection.z * second.duration) /
            totalWeight,
        };
      } else {
        mergedDirection = {
          x: (first.gazeDirection.x + second.gazeDirection.x) / 2,
          y: (first.gazeDirection.y + second.gazeDirection.y) / 2,
          z: (first.gazeDirection.z + second.gazeDirection.z) / 2,
        };
      }
    } else {
      mergedDirection = first.gazeDirection ?? second.gazeDirection;
    }
    // Drop near-zero vectors that could make downstream angular calculations unstable
    if (mergedDirection) {
      const { x, y, z } = mergedDirection;
      if (x * x + y * y + z * z < 1e-12) {
        mergedDirection = undefined;
      }
    }

    let mergedBiometrics: FixationBiometrics | undefined;
    if (first.biometrics || second.biometrics) {
      const a = first.biometrics ?? {};
      const b = second.biometrics ?? {};
      mergedBiometrics = {};

      const weightedAvg = (va: number | undefined, vb: number | undefined): number | undefined => {
        if (va !== undefined && vb !== undefined && totalWeight > 0) {
          return (va * first.duration + vb * second.duration) / totalWeight;
        }
        return va ?? vb;
      };

      const weightedVec = (
        va: GazeDirectionVector | undefined,
        vb: GazeDirectionVector | undefined,
      ): GazeDirectionVector | undefined => {
        if (va && vb && totalWeight > 0) {
          return {
            x: (va.x * first.duration + vb.x * second.duration) / totalWeight,
            y: (va.y * first.duration + vb.y * second.duration) / totalWeight,
            z: (va.z * first.duration + vb.z * second.duration) / totalWeight,
          };
        }
        return va ?? vb;
      };

      mergedBiometrics.meanLeftPupilMm = weightedAvg(a.meanLeftPupilMm, b.meanLeftPupilMm);
      mergedBiometrics.meanRightPupilMm = weightedAvg(a.meanRightPupilMm, b.meanRightPupilMm);
      mergedBiometrics.meanLeftEyeOpenness = weightedAvg(
        a.meanLeftEyeOpenness,
        b.meanLeftEyeOpenness,
      );
      mergedBiometrics.meanRightEyeOpenness = weightedAvg(
        a.meanRightEyeOpenness,
        b.meanRightEyeOpenness,
      );
      mergedBiometrics.meanHeadAngularSpeed = weightedAvg(
        a.meanHeadAngularSpeed,
        b.meanHeadAngularSpeed,
      );
      mergedBiometrics.meanGazeOrigin = weightedVec(a.meanGazeOrigin, b.meanGazeOrigin);
      mergedBiometrics.meanHeadPosition = weightedVec(a.meanHeadPosition, b.meanHeadPosition);
      mergedBiometrics.leftGazeDirection = weightedVec(a.leftGazeDirection, b.leftGazeDirection);
      mergedBiometrics.rightGazeDirection = weightedVec(a.rightGazeDirection, b.rightGazeDirection);
    }

    return new Fixation(
      index,
      first.startSampleIndex,
      second.endSampleIndex,
      {
        xPercentage: weightedX,
        yPercentage: weightedY,
      },
      duration,
      startTime,
      endTime,
      mergedDirection,
      mergedBiometrics,
    );
  }
}

export class Saccade {
  constructor(
    public readonly index: number,
    public readonly from: Fixation,
    public readonly to: Fixation,
    public readonly amplitude: number,
    public readonly duration: number,
    /** Amplitude in degrees of visual angle — computed from 3D gaze vectors when available */
    public readonly amplitudeDegrees?: number,
  ) {}
}

export class FixationSaccadeSeries {
  public readonly fixations: Fixation[];
  public readonly saccades: Saccade[];
  public readonly config: FixationSaccadeConfig;
  public readonly detectedFixationCount: number;
  public readonly nominalSampleDurationSec: number;
  public readonly dropoutThresholdSec: number;

  private readonly samples: EyeMovementSample[];
  private readonly dropoutBoundaryAfterSampleIndices: Set<number>;

  constructor(gazeData: GazeWithDelta[] = [], config?: Partial<FixationSaccadeConfig>) {
    const requestedConfig = { ...DEFAULT_FIXATION_SACCADE_CONFIG, ...config };
    const positiveSampleDurations = extractPositiveSampleDurations(gazeData);
    const observedNominalSampleDurationSec = calculateMedian(positiveSampleDurations);
    const fallbackNominalSampleDurationSec =
      requestedConfig.samplingRateHz > 0 ? 1 / requestedConfig.samplingRateHz : MIN_SAMPLE_DURATION;

    this.nominalSampleDurationSec = Math.max(
      observedNominalSampleDurationSec ?? fallbackNominalSampleDurationSec,
      MIN_SAMPLE_DURATION,
    );
    this.dropoutThresholdSec =
      this.nominalSampleDurationSec *
      normalizeDropoutThresholdMultiplier(requestedConfig.dropoutThresholdMultiplier);
    this.config = {
      ...requestedConfig,
      minSamplesPerFixation: normalizeMinimumSampleCount(requestedConfig.minSamplesPerFixation),
      dropoutThresholdMultiplier: normalizeDropoutThresholdMultiplier(
        requestedConfig.dropoutThresholdMultiplier,
      ),
      samplingRateHz: 1 / this.nominalSampleDurationSec,
    };
    this.samples = this.normalizeSamples(gazeData);
    this.dropoutBoundaryAfterSampleIndices = new Set(
      this.samples.filter((sample) => sample.hasDropoutAfter).map((sample) => sample.index),
    );

    const detectedFixations = this.identifyFixations();
    this.detectedFixationCount = detectedFixations.length;
    const mergedFixations = this.mergeAdjacentFixations(detectedFixations);

    this.fixations = mergedFixations;
    this.saccades = this.buildSaccades(mergedFixations);
  }

  get mergedFixationCount(): number {
    return this.fixations.length;
  }

  getTotalDuration(): number {
    if (!this.samples.length) {
      return 0;
    }
    return this.samples[this.samples.length - 1].endTime;
  }

  getFixationDurations(): number[] {
    return this.fixations.map((fixation) => fixation.duration);
  }

  getAverageFixationDuration(): number {
    if (!this.fixations.length) {
      return 0;
    }
    const totalDuration = this.fixations.reduce((sum, fixation) => sum + fixation.duration, 0);
    return totalDuration / this.fixations.length;
  }

  getAverageSaccadeAmplitude(): number {
    if (!this.saccades.length) {
      return 0;
    }
    const totalAmplitude = this.saccades.reduce((sum, saccade) => sum + saccade.amplitude, 0);
    return totalAmplitude / this.saccades.length;
  }

  /** Average saccade amplitude in degrees of visual angle (from 3D gaze vectors, no FOV dependency) */
  getAverageSaccadeAmplitudeDegrees(): number | undefined {
    const withDegrees = this.saccades.filter(
      (s): s is Saccade & { amplitudeDegrees: number } => s.amplitudeDegrees !== undefined,
    );
    if (!withDegrees.length) return undefined;
    return withDegrees.reduce((sum, s) => sum + s.amplitudeDegrees, 0) / withDegrees.length;
  }

  private normalizeSamples(gazeData: GazeWithDelta[]): EyeMovementSample[] {
    if (!gazeData?.length) {
      return [];
    }

    const samples: EyeMovementSample[] = [];
    let timeCursor = 0;

    gazeData.forEach((entry, index) => {
      if (!isPercentagePoint(entry?.gazePercentageData)) {
        return;
      }
      if (!isValidGazeSample(entry.gazePercentageData)) {
        return;
      }
      const normalizedPoint: PercentagePoint = {
        xPercentage: normalizePercentageCoordinate(entry.gazePercentageData.xPercentage),
        yPercentage: normalizePercentageCoordinate(entry.gazePercentageData.yPercentage),
      };
      const rawDuration = sanitizeDuration(entry?.delta);
      const hasDropoutAfter = rawDuration > this.dropoutThresholdSec;
      // Treat dropped-frame spans as missing time, not additional fixation evidence.
      const duration = hasDropoutAfter ? this.nominalSampleDurationSec : rawDuration;
      const gapAfterDuration = hasDropoutAfter ? Math.max(0, rawDuration - duration) : 0;
      const startTime = timeCursor;
      const endTime = timeCursor + duration;

      const gazeDirection = isValidGazeDirection(entry.gazeDirectionVector)
        ? entry.gazeDirectionVector
        : undefined;
      const leftGazeDirection = isValidGazeDirection(entry.leftGazeDirectionVector)
        ? entry.leftGazeDirectionVector
        : undefined;
      const rightGazeDirection = isValidGazeDirection(entry.rightGazeDirectionVector)
        ? entry.rightGazeDirectionVector
        : undefined;
      const gazeOrigin = isValidGazeDirection(entry.gazeOrigin) ? entry.gazeOrigin : undefined;
      const headPosition = isValidGazeDirection(entry.headPosition)
        ? entry.headPosition
        : undefined;
      const headAngularSpeed =
        isFiniteNumber(entry.headAngularSpeed) && entry.headAngularSpeed >= 0
          ? entry.headAngularSpeed
          : undefined;

      const leftEyeOpenness =
        isFiniteNumber(entry.leftEyeOpenness) && entry.leftEyeOpenness >= 0
          ? entry.leftEyeOpenness
          : undefined;
      const rightEyeOpenness =
        isFiniteNumber(entry.rightEyeOpenness) && entry.rightEyeOpenness >= 0
          ? entry.rightEyeOpenness
          : undefined;
      const leftPupilMm =
        isFiniteNumber(entry.leftPupilMm) && entry.leftPupilMm > 0 ? entry.leftPupilMm : undefined;
      const rightPupilMm =
        isFiniteNumber(entry.rightPupilMm) && entry.rightPupilMm > 0
          ? entry.rightPupilMm
          : undefined;

      samples.push({
        index,
        point: normalizedPoint,
        duration,
        rawDuration,
        gapAfterDuration,
        hasDropoutAfter,
        startTime,
        endTime,
        gazeDirection,
        leftGazeDirection,
        rightGazeDirection,
        gazeOrigin,
        headPosition,
        headAngularSpeed,
        leftEyeOpenness,
        rightEyeOpenness,
        leftPupilMm,
        rightPupilMm,
      });

      timeCursor = endTime + gapAfterDuration;
    });

    return samples;
  }

  private identifyFixations(): Fixation[] {
    const fixations: Fixation[] = [];
    const minimumSampleCount = this.config.minSamplesPerFixation;

    if (!this.samples.length) {
      return fixations;
    }

    let windowStart = 0;

    while (windowStart < this.samples.length) {
      let windowEnd = windowStart;
      let windowDuration = 0;

      while (
        windowEnd < this.samples.length &&
        (windowDuration < this.config.minFixationDuration ||
          windowEnd - windowStart < minimumSampleCount)
      ) {
        if (windowEnd > windowStart && this.samples[windowEnd - 1].hasDropoutAfter) {
          break;
        }
        windowDuration += this.samples[windowEnd].duration;
        windowEnd += 1;
      }

      if (
        windowDuration < this.config.minFixationDuration ||
        windowEnd - windowStart < minimumSampleCount
      ) {
        windowStart += 1;
        continue;
      }

      let windowSamples = this.samples.slice(windowStart, windowEnd);
      let dispersion = calculateDispersion(windowSamples);

      if (dispersion > this.config.dispersionThreshold) {
        windowStart += 1;
        continue;
      }

      while (windowEnd < this.samples.length) {
        if (this.samples[windowEnd - 1].hasDropoutAfter) {
          break;
        }

        const expandedSamples = this.samples.slice(windowStart, windowEnd + 1);
        const expandedDispersion = calculateDispersion(expandedSamples);
        if (expandedDispersion >= this.config.dispersionThreshold) {
          break;
        }
        windowSamples = expandedSamples;
        windowDuration += this.samples[windowEnd].duration;
        windowEnd += 1;
        dispersion = expandedDispersion;
      }

      const fixationIndex = fixations.length;
      const lastIndex = windowEnd - 1;
      fixations.push(this.buildFixation(windowStart, lastIndex, fixationIndex));
      windowStart = windowEnd;
    }

    return fixations;
  }

  private buildFixation(
    startSampleIndex: number,
    endSampleIndex: number,
    fixationIndex: number,
  ): Fixation {
    const slice = this.samples.slice(startSampleIndex, endSampleIndex + 1);
    const centroid = calculateWeightedCentroid(slice);
    const startTime = slice[0].startTime;
    const endTime = slice[slice.length - 1].endTime;
    const duration = Math.max(endTime - startTime, MIN_SAMPLE_DURATION);

    const directionCentroid = calculateWeightedDirectionCentroid(slice);
    const biometrics = computeBiometrics(slice);
    return new Fixation(
      fixationIndex,
      slice[0].index,
      slice[slice.length - 1].index,
      centroid,
      duration,
      startTime,
      endTime,
      directionCentroid,
      biometrics,
    );
  }

  private mergeAdjacentFixations(fixations: Fixation[]): Fixation[] {
    if (fixations.length <= 1) {
      return fixations.map((fixation, index) => fixation.withIndex(index));
    }

    const mergeGapThreshold = this.nominalSampleDurationSec;
    // Fallback: degrees-per-percent for each axis (used when 3D vectors are unavailable)
    const degPerPctX = this.config.headsetHFOV / 100;
    const degPerPctY = this.config.headsetVFOV / 100;

    const merged: Fixation[] = [];
    let current = fixations[0];

    for (let i = 1; i < fixations.length; i += 1) {
      const next = fixations[i];
      const temporalGap = next.startTime - current.endTime;
      const crossesDropoutBoundary = this.dropoutBoundaryAfterSampleIndices.has(
        current.endSampleIndex,
      );

      if (crossesDropoutBoundary) {
        merged.push(current.withIndex(merged.length));
        current = next;
        continue;
      }

      // Compute spatial distance in degrees of visual angle
      let distanceDegrees: number;
      if (current.gazeDirection && next.gazeDirection) {
        // Use true angular distance from 3D gaze direction vectors
        distanceDegrees = angularDistanceDegrees(current.gazeDirection, next.gazeDirection);
      } else {
        // Fallback: estimate from percentage coordinates and FOV
        const dxPct = next.position.xPercentage - current.position.xPercentage;
        const dyPct = next.position.yPercentage - current.position.yPercentage;
        distanceDegrees = Math.sqrt((dxPct * degPerPctX) ** 2 + (dyPct * degPerPctY) ** 2);
      }

      if (temporalGap <= mergeGapThreshold && distanceDegrees <= this.config.mergeDistanceDegrees) {
        current = Fixation.merge(current, next, current.index);
        continue;
      }

      merged.push(current.withIndex(merged.length));
      current = next;
    }

    merged.push(current.withIndex(merged.length));
    return merged;
  }

  private buildSaccades(fixations: Fixation[]): Saccade[] {
    if (fixations.length < 2) {
      return [];
    }

    const saccades: Saccade[] = [];
    for (let i = 1; i < fixations.length; i += 1) {
      const previous = fixations[i - 1];
      const current = fixations[i];
      const amplitude = distanceBetweenPoints(previous.position, current.position);
      const duration = Math.max(current.startTime - previous.endTime, 0);

      // Compute amplitude in degrees from 3D gaze direction vectors (no FOV dependency)
      let amplitudeDegrees: number | undefined;
      if (previous.gazeDirection && current.gazeDirection) {
        amplitudeDegrees = angularDistanceDegrees(previous.gazeDirection, current.gazeDirection);
      }

      saccades.push(new Saccade(i - 1, previous, current, amplitude, duration, amplitudeDegrees));
    }

    return saccades;
  }
}

export { distanceBetweenPoints };
